/**
 * useGroupJoinRequests — pending join requests for a group plus the review
 * actions, mirroring mobile's groupDetails userApproval handlers.
 *
 * Lazily loaded: nothing is fetched until an admin actually opens the tab.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import groupService from '@wlb/shared/api/services/groupService';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { ToastService } from '@wlb/shared/platform/notifier';
import { getApiErrorMessage } from '@wlb/shared/utils/apiErrorHandler';
import type { ApiError } from '@wlb/shared/types/api';
import type { PendingApprovalRequest } from '@wlb/shared/types/groupDetails';

export interface UseGroupJoinRequestsResult {
  requests: PendingApprovalRequest[];
  totalCount: number;
  loading: boolean;
  error: string;
  reviewingId: number | null;
  reload: () => void;
  approve: (groupJoinRequestId: number) => void;
  decline: (groupJoinRequestId: number) => void;
}

export function useGroupJoinRequests(
  groupId: string | undefined,
  enabled: boolean,
  onApproved?: () => void,
): UseGroupJoinRequestsResult {
  const { t } = useTranslation();
  const [requests, setRequests] = useState<PendingApprovalRequest[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [reviewingId, setReviewingId] = useState<number | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const onApprovedRef = useRef(onApproved);

  useEffect(() => {
    onApprovedRef.current = onApproved;
  }, [onApproved]);

  const reload = useCallback(() => setReloadToken(token => token + 1), []);

  useEffect(() => {
    if (!enabled || !groupId) return;

    let cancelled = false;
    setLoading(true);
    setError('');
    groupService
      .getGroupJoinRequests(groupId)
      .then(result => {
        if (cancelled) return;
        setRequests(result.data);
        setTotalCount(result.totalCount);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setRequests([]);
        setTotalCount(0);
        setError(getApiErrorMessage(err as ApiError) || t('common.errors.generic'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [groupId, enabled, reloadToken, t]);

  const review = useCallback(
    (groupJoinRequestId: number, action: 'Approve' | 'Reject') => {
      if (reviewingId != null) return;
      if (!groupJoinRequestId || groupJoinRequestId <= 0) {
        ToastService.show({ type: 'error', message: t('common.errors.generic') });
        return;
      }
      setReviewingId(groupJoinRequestId);
      groupService
        .reviewJoinRequest(groupJoinRequestId, action)
        .then(() => {
          setRequests(prev => prev.filter(r => r.groupJoinRequestId !== groupJoinRequestId));
          setTotalCount(count => Math.max(0, count - 1));
          // Approving adds a member, so the header counts need a refresh.
          if (action === 'Approve') onApprovedRef.current?.();
        })
        .catch((err: unknown) => {
          ToastService.show({
            type: 'error',
            message: getApiErrorMessage(err as ApiError) || t('common.errors.generic'),
          });
        })
        .finally(() => setReviewingId(null));
    },
    [reviewingId, t],
  );

  const approve = useCallback(
    (groupJoinRequestId: number) => review(groupJoinRequestId, 'Approve'),
    [review],
  );
  const decline = useCallback(
    (groupJoinRequestId: number) => review(groupJoinRequestId, 'Reject'),
    [review],
  );

  return { requests, totalCount, loading, error, reviewingId, reload, approve, decline };
}
